import * as React from 'react';
import { TabsValue } from '../../../theming';
import { FixMeLater } from '../../../utils';
import { ButtonBaseProps } from '../../ButtonBase/ButtonBase';
import { ScrollerItem } from '../../Scroller';
import { Styler, StylerProps } from '../../Styler';
import { useTabs } from '../Tabs.context';

export interface TabProps extends StylerProps, ButtonBaseProps {
    /**
     * The tab label.
     */
    children?: React.ReactNode;

    /**
     * The unique id of the `tab` component, which should also be provided to the any tab panel component which this tab controls.
     * Used for accessibility markup.
     */
    id?: string;

    /**
     * The unique id of the `tab panel` component which is controlled by this tab.
     * Used for accessibility markup.
     */
    panelId?: string;

    /**
     * A unique value for the tab.
     */
    value: TabsValue;

    /**
     * @ignore
     */
    index?: number;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
}

export function Tab(props: Readonly<TabProps>): React.JSX.Element {
    const { children, id, panelId, value, onClick, index, ref, ...other } = props;
    const { onChange, onKeyDown, value: activeValue, getTabId, getPanelId } = useTabs();

    const active = value === activeValue;

    const handleClick = (event: React.SyntheticEvent): void => {
        if (!active && value !== undefined) {
            onChange?.(event, value);
        }
        onClick?.(event);
    };

    return (
        <ScrollerItem active={active} data-tab-list-item={true}>
            <Styler
                ref={ref}
                button={true}
                component="button"
                role="tab"
                id={id || (getTabId ? getTabId(value) : undefined)}
                aria-controls={panelId || (getPanelId ? getPanelId(value) : undefined)}
                aria-selected={active}
                onClick={handleClick}
                onKeyDown={onKeyDown}
                tabIndex={active ? 0 : -1}
                data-active={active || undefined}
                data-index={index}
                data-tab-button={true}
                {...other}
            >
                {children}
            </Styler>
        </ScrollerItem>
    );
}
